import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Tag, Share2 } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useArtigo, useArtigosRelacionados, formatarData } from "@/hooks/useBlog";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";

export default function BlogArticle() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { data: artigo, isLoading, error } = useArtigo(slug || "");
  const { data: relacionados } = useArtigosRelacionados(
    artigo?.categoria_id || null,
    artigo?.id || ""
  );

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: artigo?.titulo,
          text: artigo?.resumo || "",
          url: window.location.href,
        });
      } catch (err) {
        console.log("Erro ao compartilhar:", err);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Link copiado para a área de transferência!");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container py-12">
          <Skeleton className="h-8 w-48 mb-8" />
          <Skeleton className="h-64 w-full rounded-xl mb-8" />
          <Skeleton className="h-10 w-3/4 mb-4" />
          <Skeleton className="h-4 w-48 mb-8" />
          <div className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !artigo) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container py-24 text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">
            Artigo não encontrado
          </h1>
          <p className="text-muted-foreground mb-8">
            O artigo que você procura não existe ou foi removido.
          </p>
          <Button onClick={() => navigate("/blog")}>Voltar ao Blog</Button>
        </div>
        <Footer />
      </div>
    );
  }

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: artigo.titulo,
    description: artigo.resumo || artigo.seo_description,
    image: artigo.imagem_destaque_url,
    datePublished: artigo.data_publicacao,
    dateModified: artigo.updated_at,
    author: {
      "@type": "Organization",
      name: "Aceena",
      url: "https://www.aceena.com.br",
    },
    publisher: {
      "@type": "Organization",
      name: "Aceena",
      logo: {
        "@type": "ImageObject",
        url: "https://www.aceena.com.br/simbolo-verde.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://www.aceena.com.br/blog/${artigo.slug}`,
    },
  };

  return (
    <>
      <Helmet>
        <title>{artigo.seo_title || artigo.titulo} | Aceena</title>
        <meta
          name="description"
          content={artigo.seo_description || artigo.resumo || ""}
        />
        <link
          rel="canonical"
          href={`https://www.aceena.com.br/blog/${artigo.slug}`}
        />
        <meta
          property="og:title"
          content={artigo.seo_title || artigo.titulo}
        />
        <meta
          property="og:description"
          content={artigo.seo_description || artigo.resumo || ""}
        />
        <meta
          property="og:url"
          content={`https://www.aceena.com.br/blog/${artigo.slug}`}
        />
        <meta property="og:type" content="article" />
        {artigo.imagem_destaque_url && (
          <meta property="og:image" content={artigo.imagem_destaque_url} />
        )}
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <article className="py-8 lg:py-12">
          <div className="container max-w-4xl">
            {/* Back button */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="mb-8"
            >
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar ao Blog
              </Link>
            </motion.div>

            {/* Featured Image */}
            {artigo.imagem_destaque_url && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-8 rounded-xl overflow-hidden bg-muted"
              >
                <img
                  src={artigo.imagem_destaque_url}
                  alt={artigo.titulo}
                  className="w-full h-auto object-contain"
                />
              </motion.div>
            )}

            {/* Header */}
            <motion.header
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-8"
            >
              <div className="flex flex-wrap items-center gap-3 mb-4">
                {artigo.categoria && (
                  <Link
                    to={`/blog?categoria=${artigo.categoria.slug}`}
                    className="inline-flex items-center gap-1.5 bg-aceena-green/10 text-aceena-green px-3 py-1 rounded-full text-sm font-medium hover:bg-aceena-green/20 transition-colors"
                  >
                    <Tag className="w-3.5 h-3.5" />
                    {artigo.categoria.nome}
                  </Link>
                )}
                <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Calendar className="w-3.5 h-3.5" />
                  {formatarData(artigo.data_publicacao)}
                </span>
              </div>
              <h1 className="text-2xl lg:text-4xl font-bold text-foreground leading-tight">
                {artigo.titulo}
              </h1>
            </motion.header>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="prose prose-lg max-w-none prose-headings:text-foreground prose-p:text-muted-foreground prose-a:text-primary prose-strong:text-foreground prose-img:rounded-xl prose-img:w-full prose-img:h-auto prose-img:object-contain"
              dangerouslySetInnerHTML={{ __html: artigo.conteudo_html }}
            />

            {/* Share */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-12 pt-8 border-t border-border"
            >
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <Button
                  variant="outline"
                  onClick={handleShare}
                  className="flex items-center gap-2"
                >
                  <Share2 className="w-4 h-4" />
                  Compartilhar
                </Button>
              </div>
            </motion.div>

            {/* Related Articles */}
            {relacionados && relacionados.length > 0 && (
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mt-16"
              >
                <h2 className="text-xl font-bold text-foreground mb-6">
                  Artigos Relacionados
                </h2>
                <div className="grid md:grid-cols-3 gap-6">
                  {relacionados.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/blog/${rel.slug}`}
                      className="group block bg-card rounded-lg overflow-hidden border border-border hover:border-primary/30 transition-all"
                    >
                      <div className="aspect-[16/10] overflow-hidden bg-muted">
                        {rel.imagem_destaque_url ? (
                          <img
                            src={rel.imagem_destaque_url}
                            alt={rel.titulo}
                            loading="lazy"
                            className="w-full h-full object-contain bg-muted group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/5 to-aceena-green/5">
                            <span className="text-2xl font-bold text-primary/20">
                              {rel.titulo.charAt(0)}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-medium text-foreground group-hover:text-primary transition-colors line-clamp-2">
                          {rel.titulo}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatarData(rel.data_publicacao)}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </motion.section>
            )}
          </div>
        </article>

        <Footer />
      </div>
    </>
  );
}
